import type { Beneficiario } from "./api";
import { roundPercentage } from "./percentage-utils";

export interface BeneficiarioLocal
  extends Omit<
    Beneficiario,
    "beneficiarioId" | "solicitudPolizaId" | "estadoId"
  > {
  id: string;
  numeroPoliza: number;
  porcentaje: number;
  rut: number;
  hasBeenModified: boolean;
}

export interface ApiBeneficiarioModification {
  id: number; // Original API beneficiary ID
  numeroPoliza: number;
  tipoBeneficiario: number;
  rut: number;
  dv: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  fechaNacimiento: string;
  sexo: number;
  parentesco: number;
  email: string;
  region: number;
  ciudad: number;
  comuna: number;
  direccion: string;
  casaDepto: string;
  celular: string;
  porcentaje: number;
}

export interface ApiBeneficiarioDeletion {
  id: number; // Original API beneficiary ID
  numeroPoliza: number;
  tipoBeneficiario?: number;
  rut: number;
  dv?: string;
  nombre?: string;
  apellidoPaterno?: string;
  apellidoMaterno?: string;
  fechaNacimiento?: string;
  parentesco?: number;
  email?: string;
  region?: number;
  ciudad?: number;
  comuna?: number;
  direccion?: string;
  casaDepto?: string;
  celular?: string;
  porcentaje?: number;
}

interface BeneficiariosStorage {
  added: BeneficiarioLocal[]; // New beneficiaries (Asignacion)
  modified: ApiBeneficiarioModification[]; // Modified API beneficiaries (Modificacion)
  deleted: ApiBeneficiarioDeletion[]; // Deleted API beneficiaries (Eliminacion)
}

function getStorage(): BeneficiariosStorage {
  if (typeof window === "undefined")
    return { added: [], modified: [], deleted: [] };

  const stored = localStorage.getItem("beneficiarios");
  if (!stored) return { added: [], modified: [], deleted: [] };

  try {
    const parsed = JSON.parse(stored);
    // Handle old format (array) by migrating to new format
    if (Array.isArray(parsed)) {
      return { added: parsed, modified: [], deleted: [] };
    }
    return parsed;
  } catch {
    return { added: [], modified: [], deleted: [] };
  }
}

function saveStorage(storage: BeneficiariosStorage): void {
  if (typeof window === "undefined") return;
  localStorage.setItem("beneficiarios", JSON.stringify(storage));
}

export function saveBeneficiario(beneficiario: BeneficiarioLocal): void {
  const storage = getStorage();
  storage.added.push(beneficiario);
  saveStorage(storage);
}

export function saveModifiedApiBeneficiario(
  beneficiario: ApiBeneficiarioModification
): void {
  const storage = getStorage();

  // Remove from deleted if it was there
  storage.deleted = storage.deleted.filter((d) => d.id !== beneficiario.id);

  // Update or add to modified
  const existingIndex = storage.modified.findIndex(
    (m) => m.id === beneficiario.id
  );
  if (existingIndex >= 0) {
    storage.modified[existingIndex] = beneficiario;
  } else {
    storage.modified.push(beneficiario);
  }

  saveStorage(storage);
}

export function markApiBeneficiarioAsModified(
  beneficiario: ApiBeneficiarioModification
): void {
  saveModifiedApiBeneficiario(beneficiario);
}

export function markApiBeneficiarioForDeletion(
  deletion: ApiBeneficiarioDeletion
): void {
  const storage = getStorage();

  // Remove from modified if it was there
  storage.modified = storage.modified.filter((m) => m.id !== deletion.id);

  // Add to deleted if not already there
  if (!storage.deleted.some((d) => d.id === deletion.id)) {
    storage.deleted.push(deletion);
  }

  saveStorage(storage);
}

export function unmarkApiBeneficiarioFromDeletion(id: number): void {
  const storage = getStorage();
  storage.deleted = storage.deleted.filter((d) => d.id !== id);
  saveStorage(storage);
}

export function isApiBeneficiarioMarkedForDeletion(id: number): boolean {
  const storage = getStorage();
  return storage.deleted.some((d) => d.id === id);
}

export function getModifiedApiBeneficiario(
  id: number
): ApiBeneficiarioModification | undefined {
  const storage = getStorage();
  return storage.modified.find((m) => m.id === id);
}

export function getBeneficiariosByPoliza(
  numeroPoliza: number
): BeneficiarioLocal[] {
  const storage = getStorage();
  return storage.added.filter((b) => b.numeroPoliza === numeroPoliza);
}

export function getAllBeneficiarios(): BeneficiarioLocal[] {
  const storage = getStorage();
  return storage.added;
}

export function getAllBeneficiariosStorage(): BeneficiariosStorage {
  return getStorage();
}

export function deleteBeneficiario(id: string): void {
  const storage = getStorage();
  storage.added = storage.added.filter((b) => b.id !== id);
  saveStorage(storage);
}

export function updateBeneficiario(
  id: string,
  updates: Partial<BeneficiarioLocal>
): void {
  const storage = getStorage();
  const index = storage.added.findIndex((b) => b.id === id);
  if (index >= 0) {
    storage.added[index] = { ...storage.added[index], ...updates };
    saveStorage(storage);
  }
}

export function clearBeneficiarios(): void {
  if (typeof window === "undefined") return;
  localStorage.removeItem("beneficiarios");
}

export function getBeneficiariosGroupedByPoliza(): Record<
  number,
  BeneficiarioLocal[]
> {
  const storage = getStorage();
  const grouped: Record<number, BeneficiarioLocal[]> = {};

  for (const beneficiario of storage.added) {
    if (!grouped[beneficiario.numeroPoliza]) {
      grouped[beneficiario.numeroPoliza] = [];
    }
    grouped[beneficiario.numeroPoliza].push(beneficiario);
  }

  return grouped;
}

export function getTotalPercentageByPoliza(
  numeroPoliza: number,
  apiBeneficiarios?: Array<{ id: number; porcentaje: number }>,
  excludeApiBeneficiarioId?: number
): number {
  const storage = getStorage();

  // Sum added beneficiaries
  const addedTotal = storage.added
    .filter((b) => b.numeroPoliza === numeroPoliza)
    .reduce((total, b) => total + b.porcentaje, 0);

  // Sum modified beneficiaries (excluding the one being edited if applicable)
  const modifiedTotal = storage.modified
    .filter(
      (b) =>
        b.numeroPoliza === numeroPoliza && b.id !== excludeApiBeneficiarioId
    )
    .reduce((total, b) => total + b.porcentaje, 0);

  // Sum API beneficiaries that haven't been modified or deleted (excluding the one being edited)
  let apiTotal = 0;
  if (apiBeneficiarios) {
    const modifiedIds = new Set(storage.modified.map((m) => m.id));
    const deletedIds = new Set(storage.deleted.map((d) => d.id));

    apiTotal = apiBeneficiarios
      .filter(
        (b) =>
          !modifiedIds.has(b.id) &&
          !deletedIds.has(b.id) &&
          b.id !== excludeApiBeneficiarioId
      )
      .reduce((total, b) => total + b.porcentaje, 0);
  }

  return roundPercentage(addedTotal + modifiedTotal + apiTotal);
}

export function hasAnyChanges(): boolean {
  const storage = getStorage();
  return (
    storage.added.length > 0 ||
    storage.modified.length > 0 ||
    storage.deleted.length > 0
  );
}

export function getActionsForPoliza(numeroPoliza: number): {
  added: BeneficiarioLocal[];
  modified: ApiBeneficiarioModification[];
  deleted: ApiBeneficiarioDeletion[];
} {
  const storage = getStorage();
  return {
    added: storage.added.filter((b) => b.numeroPoliza === numeroPoliza),
    modified: storage.modified.filter((b) => b.numeroPoliza === numeroPoliza),
    deleted: storage.deleted.filter((b) => b.numeroPoliza === numeroPoliza),
  };
}

export function replicateActionsToPolizas(
  sourcePoliza: number,
  targetPolizas: number[],
  apiBeneficiariosByPoliza: Record<
    number,
    Array<{ id: number; rut: number; porcentaje: number }>
  >
): void {
  const storage = getStorage();
  const sourceActions = getActionsForPoliza(sourcePoliza);

  const filteredTargetPolizas = targetPolizas.filter(
    (poliza) => poliza !== sourcePoliza
  );

  // For each target policy, replicate the actions
  for (const targetPoliza of filteredTargetPolizas) {
    const targetApiBeneficiarios = apiBeneficiariosByPoliza[targetPoliza] || [];

    // Replicate added beneficiaries
    for (const added of sourceActions.added) {
      const newId = `${Date.now()}-${Math.random()}-replicated-${targetPoliza}`;
      storage.added.push({
        ...added,
        id: newId,
        numeroPoliza: targetPoliza,
      });
    }

    // Replicate modifications - match by RUT
    for (const modified of sourceActions.modified) {
      const targetBeneficiario = targetApiBeneficiarios.find(
        (b) => b.rut === modified.rut
      );
      if (targetBeneficiario) {
        const existingModIndex = storage.modified.findIndex(
          (m) =>
            m.id === targetBeneficiario.id && m.numeroPoliza === targetPoliza
        );
        const modifiedCopy = {
          ...modified,
          id: targetBeneficiario.id,
          numeroPoliza: targetPoliza,
        };

        if (existingModIndex >= 0) {
          storage.modified[existingModIndex] = modifiedCopy;
        } else {
          storage.modified.push(modifiedCopy);
        }
      }
    }

    // Replicate deletions - match by RUT
    for (const deleted of sourceActions.deleted) {
      const targetBeneficiario = targetApiBeneficiarios.find(
        (b) => b.rut === deleted.rut
      );
      if (
        targetBeneficiario &&
        !storage.deleted.some((d) => d.id === targetBeneficiario.id)
      ) {
        storage.deleted.push({
          ...deleted,
          id: targetBeneficiario.id,
          numeroPoliza: targetPoliza,
        });
      }
    }
  }

  saveStorage(storage);
}

export function removeReplicatedActions(targetPolizas: number[]): void {
  const storage = getStorage();

  // Remove added beneficiaries for target policies
  storage.added = storage.added.filter(
    (b) => !targetPolizas.includes(b.numeroPoliza)
  );

  // Remove modifications for target policies
  storage.modified = storage.modified.filter(
    (m) => !targetPolizas.includes(m.numeroPoliza)
  );

  // Remove deletions for target policies
  storage.deleted = storage.deleted.filter(
    (d) => !targetPolizas.includes(d.numeroPoliza)
  );

  saveStorage(storage);
}

export function rutExistsInPoliza(
  numeroPoliza: number,
  rut: number,
  excludeId?: string | number,
  apiBeneficiarios?: Array<{ id: number; rut: number }>
): boolean {
  const storage = getStorage();

  // Check in added beneficiaries
  const existsInAdded = storage.added.some(
    (b) =>
      b.numeroPoliza === numeroPoliza && b.rut === rut && b.id !== excludeId
  );

  // Check in modified beneficiaries
  const existsInModified = storage.modified.some(
    (b) =>
      b.numeroPoliza === numeroPoliza && b.rut === rut && b.id !== excludeId
  );

  // Check in API beneficiaries (excluding deleted ones and the one being edited)
  let existsInApi = false;
  if (apiBeneficiarios) {
    const deletedIds = new Set(storage.deleted.map((d) => d.id));
    existsInApi = apiBeneficiarios.some(
      (b) => b.rut === rut && !deletedIds.has(b.id) && b.id !== excludeId
    );
  }

  return existsInAdded || existsInModified || existsInApi;
}
